import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit'

import { waitForResponse } from '@fairhq/common'

import { apiHeadersActions } from 'store/apiHeaders/apiHeadersSlice'
import { handleErrorState } from 'store/helpers/handleErrorState'
import { isClearAll } from 'store/helpers/isClearAll'
import { isRejected } from 'store/helpers/isRejected'

import { accountApi } from './accountApi'
import { AccountState, Account } from './types'

const clear = createAction('area/clear')

export const getAccounts = createAsyncThunk(
  'account/getAccounts',
  async (_, { dispatch, getState }) => {
    const accounts: any = await waitForResponse({
      callback: () => accountApi.getAccounts(getState),
    })
    const firstAccount = accounts[0]

    const firstAccountCompanyId = firstAccount?.companies?.[0]?.id
    dispatch(apiHeadersActions.setCompanyId(firstAccountCompanyId))

    const firstAccountId = accounts[0]?.id
    if (firstAccountId) {
      dispatch(apiHeadersActions.setAccountId(firstAccountId))
    }

    dispatch(
      apiHeadersActions.setSessionId(
        firstAccount?.companies?.[0]?.sessions?.[0]?.id || 0
      )
    )

    return accounts
  }
)

export const checkRole = createAsyncThunk(
  'account/checkRole',
  async (_, { getState }) =>
    waitForResponse({
      callback: () => accountApi.checkRole(getState),
      withJSON: false,
    })
)

export const createAccount = createAsyncThunk(
  'account/createAccount',
  async (account: Partial<Account>, { dispatch, getState }) => {
    const accountCreated = await waitForResponse({
      callback: () => accountApi.createAccount(getState, account),
    })
    if (accountCreated) {
      dispatch(apiHeadersActions.setAccountId(accountCreated.id))
    }
    return accountCreated
  }
)

export const sendEmailVerification = createAsyncThunk(
  'account/sendEmailVerification',
  async (userId: string, { getState }) =>
    waitForResponse({
      callback: () => accountApi.sendEmailVerification(getState, userId),
    })
)

export const requestChangePasswordEmail = createAsyncThunk(
  'account/requestChangePasswordEmail',
  async (userId: string, { getState }) =>
    waitForResponse({
      callback: () => accountApi.requestChangePasswordEmail(getState, userId),
    })
)

const initialState: Partial<AccountState> = { loading: false }

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(clear, () => initialState)
      .addCase(getAccounts.fulfilled, (state, action) => {
        state.loading = false
        state.accounts = action.payload
      })
      .addCase(createAccount.pending, state => {
        state.loading = true
      })
      .addCase(createAccount.fulfilled, state => {
        state.loading = false
      })

      .addCase(requestChangePasswordEmail.pending, state => {
        state.sending = true
        state.emailSent = false
      })
      .addCase(requestChangePasswordEmail.fulfilled, state => {
        state.sending = false
        state.emailSent = true
      })

      .addCase(checkRole.pending, state => {
        state.isCheckRolesLoading = true
      })
      .addCase(checkRole.fulfilled, state => {
        state.isCheckRolesLoading = false
        state.roleChecked = true
      })
      .addMatcher(isClearAll(), () => initialState)
      .addMatcher(isRejected('account'), handleErrorState)
  },
})

export const { actions: accountActions, reducer: accountReducer } = accountSlice
